/**
 * Mascot (design system, FOUNDATION — v5 "PIXEL SWEETHEART" §2: "the mascot
 * is the soul").
 *
 * Meet TICKER — a pixel heart-creature with a face and opinions. It anchors
 * every loading / empty / error / success state (never a bare spinner, never
 * a blank panel): the sprite in a mood plus a line of cute-dark-comedy copy
 * (the caller resolves © keys and passes `quip`).
 *
 *   <Mascot mood="sleepy" quip={resolveCopy("deck.empty") ?? ""} />
 *
 * Moods (v2/v3 values keep working — mapped onto Ticker states):
 *  - "idle"        soft bob + blinks (default);
 *  - "loading"     anxious bounce — pair with skeletons;
 *  - "ko"          COMPAT → heartbroken-cute (cracked, one tear);
 *  - "happy"       ^^ eyes, open smile;
 *  - "cheer"       happy + pixel sparkles + a victory bounce;
 *  - "heartbroken" the cracked sad face, played straight (it's a bit);
 *  - "sleepy"      closed eyes + floating Zzz;
 *  - "error"       wide eyes, zigzag mouth, one bead of sweat;
 *  - "wink"        the recovery face. Everything is fine.
 *
 * CSS-only animation (humble.css .mascot--* / .px-*) — freezes cleanly under
 * prefers-reduced-motion via the global rule.
 */
import { TickerSprite, type TickerState } from "./pixel.js";

export type MascotMood =
  | "idle"
  | "loading"
  | "ko"
  | "happy"
  | "cheer"
  | "heartbroken"
  | "sleepy"
  | "error"
  | "wink";

export interface MascotProps {
  mood?: MascotMood;
  /** Optional deadpan one-liner under the mark (resolve © keys caller-side). */
  quip?: string;
  size?: number;
  className?: string;
}

/** mood → Ticker sprite state ("ko" and "loading" are compat aliases). */
const SPRITE_STATE: Record<MascotMood, TickerState> = {
  idle: "idle",
  loading: "idle",
  ko: "heartbroken",
  happy: "happy",
  cheer: "cheer",
  heartbroken: "heartbroken",
  sleepy: "sleepy",
  error: "error",
  wink: "wink",
};

export function Mascot({ mood = "idle", quip, size = 44, className }: MascotProps) {
  return (
    <div
      className={`mascot mascot--${mood}${className ? ` ${className}` : ""}`}
      role={quip ? "status" : undefined}
      data-testid="mascot"
    >
      <span className="mascot__mark" aria-hidden="true">
        {/* the sprite frame is wider than the body — scale up so Ticker
            reads at the size callers asked for */}
        <TickerSprite state={SPRITE_STATE[mood]} size={Math.round(size * 1.45)} />
      </span>
      {quip && <span className="mascot__quip">{quip}</span>}
    </div>
  );
}
